import type { IconComponent } from '@consta/icons/Icon'
import { IconResize } from '@consta/icons/IconResize'
import { PortalWithTheme } from '@consta/uikit/PortalWithTheme'
import { useTheme } from '@consta/uikit/Theme'
import { useResize } from '@library/hooks'
import { useMove } from '@library/model'
import type { Size } from '@library/types'
import type { Nullable } from 'common/types'
import type { FC, PropsWithChildren } from 'react'
import { useCallback, useRef, useState } from 'react'

import { ModalHeader } from '../Modals'
import { RESIZE_ICON_PROPS } from './constants'
import css from './ResizableModal.css'

type ResizableMovableModalProps = {
  className?: string
  isOpen: boolean
  size?: Size
  minSize?: Size
  title: string
  onToggleOpen: (isOpen?: boolean) => void
  icon?: IconComponent
  portalContainer?: Element
}

export const ResizableMovableModal: FC<PropsWithChildren<ResizableMovableModalProps>> = ({
  className = '',
  children,
  size,
  minSize,
  isOpen,
  title,
  onToggleOpen,
  icon,
  portalContainer,
}) => {
  const [modal, setModal] = useState<HTMLDivElement>()
  const [moveAnchor, setMoveAnchor] = useState<HTMLDivElement>()
  const modalFn = useCallback((node: HTMLDivElement) => {
    setModal(node)
  }, [])
  const anchorFn = useCallback((node: HTMLDivElement) => {
    setMoveAnchor(node)
  }, [])
  const iconLeftFn = useRef<Nullable<HTMLDivElement>>(null)
  const iconRightFn = useRef<Nullable<HTMLDivElement>>(null)

  const { theme } = useTheme()
  useMove(modal, moveAnchor, window.document.body)
  useResize(modal, iconLeftFn.current ?? undefined, iconRightFn.current ?? undefined, size, minSize)

  if (!isOpen || portalContainer == null) return null

  return (
    <PortalWithTheme preset={theme} container={portalContainer}>
      <div ref={modalFn} className={`${css.container} ${className}`}>
        <IconResize ref={iconLeftFn} className={css.iconResizeLeftTop} {...RESIZE_ICON_PROPS} />
        <ModalHeader
          ref={anchorFn}
          title={title}
          icon={icon ?? null}
          onCloseClick={() => {
            onToggleOpen(false)
          }}
        />

        {children}

        <IconResize
          ref={iconRightFn}
          className={css.iconResizeRightBottom}
          {...RESIZE_ICON_PROPS}
        />
      </div>
    </PortalWithTheme>
  )
}
